import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  PoundSterling,
  Share2,
  TrendingUp,
  Copy,
  CheckCircle2,
  ShieldCheck,
  ArrowRight,
  Loader2,
  Info,
  Calendar,
  Mail,
  Landmark,
  Image as ImageIcon,
  Download,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Bookmark,
  X,
} from "lucide-react";
import api, { API } from "../lib/api";
import { TrustStrip, HowItWorks, FAQ, ComplianceCard, RequestInviteForm } from "../components/affiliates/AffiliatePitch";
import usePageMeta from "../lib/usePageMeta";
import JunkFolderHint from "../components/JunkFolderHint";
import { track } from "../lib/gtag";

const TOKEN_KEY = "pg_affiliate_token";

const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00Z` : iso);
  return isNaN(d) ? String(iso).slice(0, 10) : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

const STATE_COLOURS = {
  pending: "#F7B500",
  available: "#A6FF00",
  requested: "#F7B500",
  paid: "#5EEAD4",
  held: "#EF4444",
  voided: "#9CA8B8",
  refunded_after_payout: "#EF4444",
};

const PERKS = [
  {
    icon: PoundSterling,
    title: "10% into your pot · 15% as store credit",
    body: "Every new customer's first paid order through your link puts 10% of the item price (after any discount, shipping excluded) into your pot. Cash out as store credit and it's worth 1.5× — 15% of the order.",
  },
  {
    icon: Calendar,
    title: "Cash out when you like",
    body: "Commission is pending for 5 days, then available. Instant store-credit code at any time, or a bank transfer from £20 — approved by us, then 3–5 working days to land.",
  },
  {
    icon: TrendingUp,
    title: "30-day attribution · one owner per customer",
    body: "A 30-day cookie, set server-side. Customers are matched by email, so a person is only ever paid for once — no double-dipping, no disputes.",
  },
  {
    icon: ShieldCheck,
    title: "British, compliant, transparent",
    body: "Research-use-only framing, UK ASA-compliant disclosures, clear programme rules and a real-time dashboard.",
  },
];

function Hero({ inviteOnly = false }) {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-10">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="pg-micro">// Affiliate Programme</div>
        {inviteOnly && (
          <span
            className="inline-flex items-center gap-1.5 pg-mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-sm"
            style={{ background: "rgba(166,255,0,0.08)", border: "1px solid rgba(166,255,0,0.4)", color: "#A6FF00" }}
            data-testid="aff-invite-only-pill"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#A6FF00] animate-pulse" /> Invite-only · applications open
          </span>
        )}
      </div>
      <h1
        className="text-[#F7FAFC] text-4xl sm:text-5xl lg:text-6xl pg-display leading-[1.05] mb-5"
        style={{ fontFamily: "'Exo 2'" }}
      >
        Earn <span className="text-[#A6FF00]">10%</span> on every new customer you send.
        <br />
        Or <span className="text-[#A6FF00]">15%</span> as instant store credit.
      </h1>
      <p className="text-[#9CA8B8] max-w-2xl text-[15px] leading-relaxed mb-6">
        Help UK research labs discover cleaner peptides — same-day dispatch, 99%+ purity, a CoA on every vial —
        and build a pot you cash out whenever you like. No targets, no tiers, no waiting for payday.
      </p>
      {inviteOnly && (
        <div className="flex items-center gap-3 flex-wrap">
          <a href="#apply" className="pg-btn-primary text-sm" data-testid="aff-hero-apply-cta">
            Request an invite <ArrowRight size={16} />
          </a>
          <span className="text-[#9CA8B8] text-xs">Already a partner? Sign-in form is below.</span>
        </div>
      )}
    </div>
  );
}

function Perks() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PERKS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="pg-card p-5 h-full">
            <Icon size={18} className="text-[#00C2D6] mb-3" />
            <div
              className="text-[#F7FAFC] text-lg pg-heading mb-1.5"
              style={{ fontFamily: "'Rajdhani'" }}
            >
              {title}
            </div>
            <div className="text-[#9CA8B8] text-sm leading-relaxed">{body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EarningsExample() {
  const [spend, setSpend] = useState(240);
  const commission = +(spend * 0.1).toFixed(2);
  const credit = +(commission * 1.5).toFixed(2);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-12">
      <div
        className="pg-card p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-5 gap-8 items-center"
        data-testid="affiliate-earnings-example"
      >
        <div className="lg:col-span-3">
          <div className="pg-micro mb-2">// Earnings example</div>
          <h3
            className="text-[#F7FAFC] text-2xl sm:text-3xl pg-heading mb-3"
            style={{ fontFamily: "'Rajdhani'" }}
          >
            Here's what <span className="text-[#A6FF00]">10%</span> looks like in pounds.
          </h3>
          <p className="text-[#9CA8B8] text-sm leading-relaxed mb-4">
            Move the slider to see the commission on a new customer's first order through your link.
            The calculation is <strong className="text-[#F7FAFC]">10% of the item price after any discount</strong> —
            shipping is always excluded. Cash out as store credit and the pot is worth{" "}
            <strong className="text-[#A6FF00]">1.5× — 15% of the order</strong>.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="pg-micro mr-2">// Customer spend</label>
            <input
              type="range"
              min={60}
              max={800}
              step={10}
              value={spend}
              onChange={(e) => setSpend(Number(e.target.value))}
              data-testid="earnings-slider"
              className="flex-1 min-w-[180px] accent-[#A6FF00]"
            />
            <div className="pg-mono text-sm text-[#F7FAFC] min-w-[70px] text-right">£{spend}</div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3 text-sm">
            <div className="p-3 bg-[#0B1220] border border-[#1E2E44] rounded-sm">
              <div className="pg-micro mb-1">Items paid (after discount)</div>
              <div className="text-[#F7FAFC] pg-mono">£{spend.toFixed(2)}</div>
            </div>
            <div className="p-3 bg-[#0B1220] border border-[#1E2E44] rounded-sm">
              <div className="pg-micro mb-1">Into your pot · bank</div>
              <div className="text-[#F7FAFC] pg-mono font-bold" data-testid="earnings-commission">
                £{commission.toFixed(2)}
              </div>
            </div>
            <div className="p-3 bg-[#0B1220] border border-[#A6FF00]/50 rounded-sm">
              <div className="pg-micro mb-1" style={{ color: "#A6FF00" }}>
                As store credit (×1.5)
              </div>
              <div className="text-[#A6FF00] pg-mono font-bold" data-testid="earnings-credit">
                £{credit.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 p-5 bg-[#0B1220] border border-[#1E2E44] rounded-sm">
          <div className="pg-micro mb-3">// Realistic monthly scenarios</div>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-3 text-sm items-baseline" data-testid="aff-scenarios">
            <span />
            <span className="pg-micro text-right">Bank</span>
            <span className="pg-micro text-right" style={{ color: "#A6FF00" }}>Credit</span>
            {[
              ["5 orders × £180", "£90", "£135"],
              ["12 orders × £220", "£264", "£396"],
              ["30 orders × £240", "£720", "£1,080"],
            ].map(([label, bank, credit]) => (
              <React.Fragment key={label}>
                <span className="text-[#9CA8B8] whitespace-nowrap">{label}</span>
                <span className="text-[#F7FAFC] pg-mono font-bold text-right whitespace-nowrap">{bank}</span>
                <span className="text-[#A6FF00] pg-mono font-bold text-right whitespace-nowrap">{credit}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[#1E2E44] text-[11px] text-[#9CA8B8] leading-relaxed">
            Pending 5 days, then cash out any time. Research-use-only products; promotion
            must comply with UK CAP / ASA (#ad) and PECR.
          </div>
        </div>
      </div>
    </div>
  );
}

function LoginLinkForm() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSending(true);
    try {
      await api.post("/affiliates/login-request", { email: email.trim().toLowerCase() });
      setSent(true);
      setSentEmail(email.trim().toLowerCase());
      toast.success("If that email is registered, we've sent a sign-in link.");
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setSending(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      className="pg-card p-5 space-y-3"
      data-testid="affiliate-login-form"
    >
      <div className="pg-micro">// Already an affiliate?</div>
      <div
        className="text-[#F7FAFC] text-lg pg-heading"
        style={{ fontFamily: "'Rajdhani'" }}
      >
        Sign in to your dashboard
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA8B8]" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@lab.co.uk"
            data-testid="affiliate-login-email"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <button
          type="submit"
          disabled={sending || sent}
          className="pg-btn-ghost text-xs"
          data-testid="affiliate-login-submit"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : sent ? <CheckCircle2 size={14} /> : <ArrowRight size={14} />}
          {sent ? "Link sent" : "Email me a link"}
        </button>
      </div>
      {sent ? (
        <JunkFolderHint email={sentEmail} what="your sign-in link" size="sm" />
      ) : (
        <div className="text-[11px] text-[#9CA8B8] leading-relaxed">
          We'll email a secure sign-in link to the address on file. No password — no forgetting.
        </div>
      )}
    </form>
  );
}

function SignupForm({ onJoined }) {
  const [form, setForm] = useState({ name: "", email: "", website: "", audience: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alreadySent, setAlreadySent] = useState(null); // email if already-registered message sent

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Please accept the Affiliate Programme Terms to continue");
      return;
    }
    if (!form.name.trim() || !form.email.includes("@")) {
      toast.error("Please supply your name and a valid email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/affiliates/signup", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        website: form.website || null,
        audience: form.audience || null,
        accepted_terms: true,
      });
      if (data.already_registered) {
        toast.success(data.message || "We've emailed you a sign-in link.");
        setAlreadySent(form.email.trim().toLowerCase());
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      track.signUpAffiliate();
      toast.success(data.message || "Welcome — your referral link is ready");
      onJoined(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Signup failed — please try again";
      toast.error(typeof msg === "string" ? msg : "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={submit} className="pg-card p-6 sm:p-8 space-y-5" data-testid="affiliate-signup-form">
      <div>
        <div
          className="text-[#F7FAFC] text-2xl pg-heading mb-1.5"
          style={{ fontFamily: "'Rajdhani'" }}
        >
          Join the programme
        </div>
        <div className="text-[#9CA8B8] text-sm">
          Instant approval. Get your unique link + dashboard in seconds.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="pg-micro mb-1.5 block">// Full name <span className="text-[#A6FF00]">*</span></label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
            data-testid="aff-signup-name"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6] transition-colors"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// Email address <span className="text-[#A6FF00]">*</span></label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            required
            data-testid="aff-signup-email"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6] transition-colors"
          />
        </div>
      </div>
      <div>
        <label className="pg-micro mb-1.5 block">// Website / social handle</label>
        <input
          type="text"
          value={form.website}
          onChange={(e) => update("website", e.target.value)}
          placeholder="https://… or @handle"
          data-testid="aff-signup-website"
          className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6] transition-colors"
        />
      </div>
      <div>
        <label className="pg-micro mb-1.5 block">// Where will you share Peptigen Labs?</label>
        <textarea
          value={form.audience}
          onChange={(e) => setForm({ ...form, audience: e.target.value })}
          rows={3}
          placeholder="e.g. my biotech newsletter (2,400 subs), LinkedIn, my lab supplies comparison blog…"
          data-testid="aff-signup-audience"
          className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6] transition-colors resize-none"
        />
      </div>

      <label
        className="flex items-start gap-3 p-4 bg-[#0B1220] border border-[#1E2E44] rounded-sm cursor-pointer"
        data-testid="aff-signup-accept"
      >
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 accent-[#A6FF00]"
        />
        <span className="text-[#9CA8B8] text-[13px] leading-relaxed">
          I have read and accept the{" "}
          <Link
            to="/legal/affiliate-terms"
            target="_blank"
            className="text-[#00C2D6] hover:underline"
          >
            Affiliate Programme Terms
          </Link>{" "}
          and understand I must NOT make any human-consumption, medical or therapeutic claim, must
          not bid on Peptigen Labs brand keywords, and must disclose my promotions as {" "}
          <span className="text-[#F7FAFC]">#ad / #affiliate</span> (UK ASA / CAP compliance).
        </span>
      </label>

      <button
        type="submit"
        disabled={loading}
        data-testid="aff-signup-submit"
        className="pg-btn-primary w-full sm:w-auto"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {loading ? "Creating your link…" : "Get my referral link"}
      </button>
      {alreadySent && (
        <JunkFolderHint email={alreadySent} what="your sign-in link" size="sm" />
      )}
    </form>
  );
}

function Stat({ label, value, accent = "#F7FAFC" }) {
  return (
    <div className="pg-card p-5">
      <div className="pg-micro mb-2">{label}</div>
      <div
        className="text-3xl pg-heading"
        style={{ fontFamily: "'Rajdhani'", color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function CashOutPanel({ token, data, onDone }) {
  const [confirming, setConfirming] = useState(null); // "credit" | "bank" | null
  const [busy, setBusy] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [copied, setCopied] = useState(false);
  const available = Number(data.available_gbp || 0);
  const creditValue = Number(data.available_credit_gbp || 0);
  const pending = Number(data.pending_gbp || 0);

  const run = async (method) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/affiliates/cash-out?t=${encodeURIComponent(token)}`, { method });
      if (method === "credit") {
        setVoucher(res.payout);
        toast.success(`£${Number(res.payout.amount_gbp).toFixed(2)} store credit issued — code emailed to you too`);
      } else {
        toast.success("Bank transfer requested — we'll approve it and the money lands in 3–5 working days");
      }
      setConfirming(null);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cash-out failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(voucher.voucher_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy — select the code and copy manually");
    }
  };

  return (
    <div className="pg-card p-6" data-testid="aff-cashout-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[#F7FAFC] text-lg pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
            Cash out your pot
          </div>
          <p className="text-[#9CA8B8] text-xs mt-1 leading-relaxed max-w-xl" data-testid="aff-cashout-note">
            Commission is pending for {data.pending_days} days after each order, then available. Cash out the whole
            available balance whenever you like — store credit is instant and worth 1.5× the pot.
            {pending > 0 && data.next_clear_at && (
              <>
                {" "}
                Your next commission clears on <span className="text-[#F7FAFC]">{fmtDay(data.next_clear_at)}</span>.
              </>
            )}
          </p>
        </div>
        <div className="text-right">
          <div className="pg-micro mb-1" style={{ color: "#5EEAD4" }}>// Available now</div>
          <div className="text-3xl pg-heading text-[#A6FF00]" style={{ fontFamily: "'Rajdhani'" }} data-testid="aff-available-pot">
            £{available.toFixed(2)}
          </div>
        </div>
      </div>

      {voucher && (
        <div
          className="mb-4 p-4 bg-[#0B1220] border border-dashed border-[#A6FF00] rounded-sm text-center"
          data-testid="aff-voucher-issued"
        >
          <div className="pg-micro mb-1" style={{ color: "#5EEAD4" }}>// Your store-credit code · worth £{Number(voucher.amount_gbp).toFixed(2)}</div>
          <div className="text-[#A6FF00] text-2xl font-extrabold tracking-[0.1em] pg-mono" data-testid="aff-voucher-code">
            {voucher.voucher_code}
          </div>
          <button onClick={copyCode} className="pg-btn-ghost text-xs mt-2" data-testid="aff-voucher-copy">
            {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy code"}
          </button>
          <div className="text-[#9CA8B8] text-[11px] mt-2">
            Enter it in the store-credit box at checkout. Single use, never expires, postage still applies. We've emailed it to you as well.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          className="p-4 rounded-sm border flex flex-col"
          style={{ background: "rgba(166,255,0,0.06)", borderColor: "rgba(166,255,0,0.5)" }}
          data-testid="aff-cashout-credit"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[#F7FAFC] font-semibold text-sm">Store credit — instant</span>
            <span className="pg-micro" style={{ color: "#A6FF00" }}>best value</span>
          </div>
          <div className="text-[#A6FF00] pg-mono text-2xl font-bold" data-testid="aff-cashout-credit-value">
            £{creditValue.toFixed(2)}
          </div>
          <div className="text-[#9CA8B8] text-[11px] mt-1 leading-relaxed flex-1">
            1.5× your pot as a single-use code, issued the moment you confirm. Stacks with discount codes; postage still
            applies; can't be exchanged for cash.
          </div>
          {confirming === "credit" ? (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => run("credit")}
                disabled={busy}
                className="pg-btn-primary text-xs"
                data-testid="aff-cashout-credit-confirm"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Confirm £{creditValue.toFixed(2)} code
              </button>
              <button onClick={() => setConfirming(null)} disabled={busy} className="pg-btn-ghost text-xs">
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming("credit")}
              disabled={!data.can_cash_out_credit || busy}
              className="pg-btn-primary text-xs mt-3 disabled:opacity-40"
              data-testid="aff-cashout-credit-btn"
            >
              Cash out as store credit
            </button>
          )}
        </div>

        <div
          className="p-4 rounded-sm border flex flex-col"
          style={{ background: "#0B1220", borderColor: "#1E2E44" }}
          data-testid="aff-cashout-bank"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[#F7FAFC] font-semibold text-sm">Bank transfer</span>
            <span className="pg-micro text-[#9CA8B8]">from £{Number(data.bank_min_gbp || 20).toFixed(0)}</span>
          </div>
          <div className="text-[#F7FAFC] pg-mono text-2xl font-bold" data-testid="aff-cashout-bank-value">
            £{available.toFixed(2)}
          </div>
          <div className="text-[#9CA8B8] text-[11px] mt-1 leading-relaxed flex-1">
            Sent as a request for our approval; once approved it lands in your nominated account within 3–5 working
            days. Needs your bank details below.
          </div>
          {data.open_bank_request ? (
            <div className="mt-3 text-[#F7B500] text-xs" data-testid="aff-bank-request-open">
              £{Number(data.open_bank_request.amount_gbp).toFixed(2)} requested on{" "}
              {String(data.open_bank_request.requested_at || "").slice(0, 10)} — awaiting approval.
            </div>
          ) : confirming === "bank" ? (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => run("bank")}
                disabled={busy}
                className="pg-btn-primary text-xs"
                data-testid="aff-cashout-bank-confirm"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={14} />}
                Request £{available.toFixed(2)} transfer
              </button>
              <button onClick={() => setConfirming(null)} disabled={busy} className="pg-btn-ghost text-xs">
                Cancel
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => setConfirming("bank")}
                disabled={!data.can_cash_out_bank || busy}
                className="pg-btn-ghost text-xs mt-3 disabled:opacity-40"
                data-testid="aff-cashout-bank-btn"
              >
                Request bank transfer
              </button>
              {!data.can_cash_out_bank && data.bank_blocked_reason && (
                <div className="text-[#9CA8B8] text-[11px] mt-2" data-testid="aff-bank-blocked-reason">
                  {data.bank_blocked_reason}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function BankDetailsPanel({ token, affiliate, onSaved }) {
  const [form, setForm] = useState({
    bank_account_holder: affiliate.bank_account_holder || "",
    bank_name: affiliate.bank_name || "",
    bank_sort_code: affiliate.bank_sort_code || "",
    bank_account_number: affiliate.bank_account_number || "",
    bank_iban: affiliate.bank_iban || "",
    bank_swift: affiliate.bank_swift || "",
    bank_country: affiliate.bank_country || "United Kingdom",
    tax_reference: affiliate.tax_reference || "",
  });
  const [saving, setSaving] = useState(false);
  const complete = affiliate.bank_details_complete;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Strip empty strings so PATCH only touches filled-in fields.
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null)
      );
      await api.patch(`/affiliates/me?t=${encodeURIComponent(token)}`, payload);
      toast.success("Payout details saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save — check the values");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="pg-card p-6"
      data-testid="affiliate-bank-details"
    >
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Landmark size={16} className="text-[#00C2D6]" />
            <div
              className="text-[#F7FAFC] text-lg pg-heading"
              style={{ fontFamily: "'Rajdhani'" }}
            >
              Payout bank details
            </div>
          </div>
          <div className="text-[#9CA8B8] text-[13px]">
            Needed before you can request a bank transfer. UK: sort code + account number. Non-UK: IBAN + SWIFT.
          </div>
        </div>
        <span
          className="pg-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-sm border"
          style={{
            color: complete ? "#A6FF00" : "#F7B500",
            borderColor: complete ? "rgba(166,255,0,0.4)" : "rgba(247,181,0,0.4)",
            background: complete ? "rgba(166,255,0,0.06)" : "rgba(247,181,0,0.06)",
          }}
          data-testid="bank-details-status"
        >
          {complete ? "Details on file" : "Details required"}
        </span>
      </div>

      {!complete && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-[#F7B500]/10 border border-[#F7B500]/30 rounded-sm text-[12px] text-[#F7B500] leading-relaxed">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            Commission accrues regardless, but we can only pay it once these details are on file.
            Your data is stored solely for the purpose of remitting commission payments.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="pg-micro mb-1.5 block">// Account holder name *</label>
          <input
            type="text"
            value={form.bank_account_holder}
            onChange={(e) => update("bank_account_holder", e.target.value)}
            data-testid="bank-account-holder"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// Bank name</label>
          <input
            type="text"
            value={form.bank_name}
            onChange={(e) => update("bank_name", e.target.value)}
            placeholder="e.g. Monzo, Barclays"
            data-testid="bank-name"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// UK sort code (NN-NN-NN)</label>
          <input
            type="text"
            value={form.bank_sort_code}
            onChange={(e) => update("bank_sort_code", e.target.value)}
            placeholder="20-00-00"
            data-testid="bank-sort-code"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// UK account number</label>
          <input
            type="text"
            value={form.bank_account_number}
            onChange={(e) => update("bank_account_number", e.target.value)}
            placeholder="12345678"
            data-testid="bank-account-number"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// IBAN (non-UK)</label>
          <input
            type="text"
            value={form.bank_iban}
            onChange={(e) => update("bank_iban", e.target.value)}
            placeholder="GB29 NWBK 6016 1331 9268 19"
            data-testid="bank-iban"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// SWIFT / BIC (non-UK)</label>
          <input
            type="text"
            value={form.bank_swift}
            onChange={(e) => update("bank_swift", e.target.value)}
            placeholder="NWBKGB2L"
            data-testid="bank-swift"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// Country</label>
          <input
            type="text"
            value={form.bank_country}
            onChange={(e) => update("bank_country", e.target.value)}
            data-testid="bank-country"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// UTR / VAT number (optional)</label>
          <input
            type="text"
            value={form.tax_reference}
            onChange={(e) => update("tax_reference", e.target.value)}
            placeholder="For your invoicing / self-assessment"
            data-testid="tax-reference"
            className="w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#00C2D6]"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        data-testid="bank-save-submit"
        className="pg-btn-primary mt-5"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
        {saving ? "Saving…" : "Save payout details"}
      </button>
      <div className="mt-3 text-[11px] text-[#9CA8B8] leading-relaxed">
        Affiliates are independent contractors. You are solely responsible for your own tax reporting
        (self-assessment / VAT). See{" "}
        <Link to="/legal/affiliate-terms" className="text-[#00C2D6] hover:underline">
          programme terms clause 5
        </Link>
        .
      </div>
    </form>
  );
}

function ShareCardButton({ token, referralUrl, slug, onEngage }) {
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState("square");
  const [imgCopied, setImgCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);

  const CAPTION_TEMPLATE = `Peptigen Labs — British research-peptide supplier. Independently purity-tested, same-day UK dispatch (before 14:00). My referral link below — use code WELCOME10 for 10% off your first order.

${referralUrl || `https://peptigenlabs.co.uk/?ref=${slug || ""}`}

Research use only · not for human or animal consumption · #ad · #affiliate`;

  // Fetch the PNG blob once per click and cache it briefly so both
  // "Download" and "Share" and "Copy image" hit the same request.
  const fetchBlob = async () => {
    const res = await fetch(
      `${API}/affiliates/social-card?t=${encodeURIComponent(token)}&size=${size}`,
      { cache: "no-store" }
    );
    if (!res.ok) throw new Error("bad");
    return await res.blob();
  };

  const filename = () => `peptigen-affiliate-${slug || "card"}-${size}.png`;

  // Desktop-first download: saveAs via a blob URL. iOS Safari ignores the
  // `download` attribute, so there we fall back to opening the PNG in a new
  // tab where the user can long-press → "Save to Photos".
  const download = async () => {
    setLoading(true);
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        window.open(url, "_blank", "noopener");
        toast.success("Long-press the image and choose 'Save to Photos'.");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename();
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Share card downloaded — remember to tag #ad when you post.");
      }
      onEngage?.();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      toast.error("Could not generate — please retry");
    } finally {
      setLoading(false);
    }
  };

  // Web Share API — best experience on mobile (native share sheet with
  // Instagram, WhatsApp, Mail, AirDrop, etc). Falls back to download.
  const share = async () => {
    setLoading(true);
    try {
      const blob = await fetchBlob();
      const file = new File([blob], filename(), { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Peptigen Labs — research peptide supplier",
          text: CAPTION_TEMPLATE,
        });
        toast.success("Shared");
        onEngage?.();
      } else {
        // Desktop browsers typically lack file-share support → download.
        await download();
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Share not available — downloaded PNG instead.");
        await download();
      }
    } finally {
      setLoading(false);
    }
  };

  const copyImage = async () => {
    try {
      const blob = await fetchBlob();
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        setImgCopied(true);
        setTimeout(() => setImgCopied(false), 1800);
        toast.success("Image copied — paste into Instagram / X / email.");
        onEngage?.();
        return;
      }
      throw new Error("unsupported");
    } catch (err) {
      // Graceful fallback — the user probably wants the image regardless.
      toast.message(
        "Your browser can't copy images to clipboard — downloading instead.",
        { duration: 4000 }
      );
      await download();
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(CAPTION_TEMPLATE);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 1800);
      toast.success("Caption copied — ASA-compliant disclosure included.");
    } catch {
      // Fallback: create a transient <textarea> so document.execCommand('copy') works
      try {
        const ta = document.createElement("textarea");
        ta.value = CAPTION_TEMPLATE;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        ta.remove();
        setCaptionCopied(true);
        setTimeout(() => setCaptionCopied(false), 1800);
        toast.success("Caption copied.");
      } catch {
        toast.error("Could not copy caption — please select the preview and copy manually.");
      }
    }
  };

  const hasShare = typeof navigator !== "undefined" && "share" in navigator;
  const preview = `${API}/affiliates/social-card?t=${encodeURIComponent(token)}&size=${size}`;
  return (
    <div className="pg-card p-6" data-testid="affiliate-share-card">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ImageIcon size={16} className="text-[#00C2D6]" />
            <div
              className="text-[#F7FAFC] text-lg pg-heading"
              style={{ fontFamily: "'Rajdhani'" }}
            >
              Branded share card
            </div>
          </div>
          <div className="text-[#9CA8B8] text-[13px]">
            Auto-generated Peptigen Labs image with your link + the mandatory research-use and
            #ad disclosures baked in. Post it to Instagram, X, Reddit or your newsletter.
          </div>
        </div>
        <div className="flex items-center gap-1 pg-mono text-[11px]">
          {["square", "story", "landscape"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSize(s)}
              className="px-2.5 py-1 rounded-sm border uppercase tracking-wider"
              style={{
                borderColor: s === size ? "#00C2D6" : "#1E2E44",
                color: s === size ? "#00C2D6" : "#9CA8B8",
                background: s === size ? "rgba(0,194,214,0.08)" : "transparent",
              }}
              data-testid={`share-card-size-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-3 flex items-center justify-center">
        <img
          src={preview}
          alt="Peptigen Labs referral card preview"
          className="max-w-full max-h-[320px] rounded-sm"
          data-testid="share-card-preview"
        />
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {hasShare && (
          <button
            type="button"
            onClick={share}
            disabled={loading}
            data-testid="share-card-share"
            className="pg-btn-primary"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
            Share
          </button>
        )}
        <button
          type="button"
          onClick={download}
          disabled={loading}
          data-testid="share-card-download"
          className={hasShare ? "pg-btn-ghost text-xs" : "pg-btn-primary"}
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Download PNG
        </button>
        <button
          type="button"
          onClick={copyImage}
          data-testid="share-card-copy-image"
          className="pg-btn-ghost text-xs"
        >
          {imgCopied ? <CheckCircle2 size={14} /> : <ImageIcon size={14} />}
          {imgCopied ? "Image copied" : "Copy image"}
        </button>
        <button
          type="button"
          onClick={copyCaption}
          data-testid="share-card-copy-caption"
          className="pg-btn-ghost text-xs"
        >
          {captionCopied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
          {captionCopied ? "Caption copied" : "Copy caption"}
        </button>
      </div>

      <div className="mt-4 p-3 bg-[#0B1220] border border-[#1E2E44] rounded-sm">
        <div className="pg-micro mb-2">// Caption preview (what gets copied)</div>
        <pre
          className="text-[12px] text-[#9CA8B8] whitespace-pre-wrap leading-relaxed font-normal"
          style={{ fontFamily: "inherit" }}
          data-testid="share-card-caption-preview"
        >
{CAPTION_TEMPLATE}
        </pre>
      </div>

      <div className="mt-3 text-[11px] text-[#9CA8B8] leading-relaxed">
        Every card carries the mandatory "RESEARCH USE ONLY · #ad · #affiliate" footer. You still
        need to use native platform disclosure tools (Instagram Paid Partnership, TikTok Branded
        Content, YouTube #ad) where available.
      </div>
    </div>
  );
}

function StepsStrip({ steps, earned, earnedAmount }) {
  const items = [
    {
      key: "copy",
      done: steps.copy,
      title: "Copy your link",
      body: "Grab your unique referral link below and drop it anywhere — bio, newsletter, DMs.",
    },
    {
      key: "card",
      done: steps.card,
      title: "Share the card",
      body: "Download or share the ready-made branded card — compliant caption included.",
    },
    {
      key: "earn",
      done: earned,
      title: "Cash out when you like",
      body: earned
        ? `£${Number(earnedAmount || 0).toFixed(2)} earned so far — pending 5 days, then yours to cash out.`
        : "10% of every order through your link lands in your pot automatically — cash out as credit or bank transfer.",
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="aff-steps-strip">
      {items.map((it, i) => (
        <div key={it.key} className="pg-card p-5 flex items-start gap-3" data-testid={`aff-step-${i + 1}`}>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border"
            style={{
              borderColor: it.done ? "#A6FF00" : "#1E2E44",
              background: it.done ? "rgba(166,255,0,0.1)" : "#0B1220",
              color: it.done ? "#A6FF00" : "#9CA8B8",
            }}
          >
            {it.done ? <CheckCircle2 size={16} /> : <span className="pg-mono text-xs">{i + 1}</span>}
          </div>
          <div>
            <div className="text-[#F7FAFC] text-sm font-semibold mb-0.5">{it.title}</div>
            <div className="text-[#9CA8B8] text-xs leading-relaxed">{it.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BookmarkTip({ slug }) {
  const key = `pg_aff_bookmark_tip_${slug}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(key) === "1");
  if (hidden) return null;
  const loginUrl = `${window.location.origin}/affiliates`;
  const dismiss = () => {
    localStorage.setItem(key, "1");
    setHidden(true);
  };
  return (
    <div
      className="flex items-start gap-3 p-3 sm:p-4 bg-[#0B1220] border border-[#A6FF00]/40 rounded-sm"
      data-testid="aff-bookmark-tip"
    >
      <Bookmark size={16} className="text-[#A6FF00] shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1 text-[#9CA8B8] text-xs sm:text-sm leading-relaxed">
        <span className="text-[#F7FAFC] font-semibold">Bookmark this page to monitor your earnings.</span>{" "}
        Your sign-in page is{" "}
        <a href={loginUrl} className="text-[#5EEAD4] hover:underline break-all" data-testid="aff-bookmark-login-url">
          {loginUrl.replace(/^https?:\/\//, "")}
        </a>
        {" "}— when you come back, tap “Sign in to your dashboard”, enter your email and we'll send a fresh link. No password to remember.
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-[#9CA8B8] hover:text-[#F7FAFC] shrink-0 transition-colors"
        aria-label="Dismiss bookmark tip"
        data-testid="aff-bookmark-dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [steps, setSteps] = useState({ copy: false, card: false });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/affiliates/dashboard?t=${encodeURIComponent(token)}`);
      setData(d);
    } catch {
      toast.error("Your affiliate session has expired — please sign in again");
      localStorage.removeItem(TOKEN_KEY);
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const slugKey = data?.affiliate?.slug || "";
  useEffect(() => {
    if (!slugKey) return;
    setSteps({
      copy: localStorage.getItem(`pg_aff_step_copy_${slugKey}`) === "1",
      card: localStorage.getItem(`pg_aff_step_card_${slugKey}`) === "1",
    });
  }, [slugKey]);
  const markStep = (k) => {
    if (slugKey) localStorage.setItem(`pg_aff_step_${k}_${slugKey}`, "1");
    setSteps((s) => (s[k] ? s : { ...s, [k]: true }));
  };

  const writeToClipboard = async (text) => {
    // Primary: async clipboard API (HTTPS / modern browsers)
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        // Modern clipboard API blocked (insecure context, permission denied).
        // Falls through to legacy textarea+execCommand below.
        console.debug("[Affiliates] clipboard.writeText blocked, using legacy fallback:", err?.message || err);
      }
    }
    // Legacy fallback for older / non-secure contexts
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  };

  const copyLink = async () => {
    if (!data?.referral_url) return;
    const ok = await writeToClipboard(data.referral_url);
    if (ok) {
      setCopied(true);
      markStep("copy");
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Could not copy — please select the link and copy manually");
    }
  };

  const shareLink = async () => {
    if (!data?.referral_url) return;
    const shareData = {
      title: "Peptigen Labs — research peptides, UK same-day dispatch",
      text:
        "Peptigen Labs supplies research-use-only peptides from a UK lab. " +
        "Same-day dispatch before 14:00.\n\n" +
        `My link: ${data.referral_url}\n` +
        "Use code WELCOME10 for 10% off your first order.\n\n" +
        "(Research use only — not for human or animal consumption.)",
    };

    // Mobile / supported desktop: native share sheet
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share(shareData);
        markStep("copy");
        toast.success("Shared — remember to tag #ad on social posts.");
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled — silent
        // any other error → fall through to clipboard copy
      }
    }

    // Fallback: copy link to clipboard so user can paste anywhere
    const ok = await writeToClipboard(data.referral_url);
    if (ok) {
      setCopied(true);
      markStep("copy");
      toast.success("Link copied — paste it anywhere to share.");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Could not share — please select the link and copy manually.");
    }
  };

  if (loading || !data) {
    return (
      <div className="pg-card p-10 text-center" data-testid="aff-dashboard-loading">
        <Loader2 size={20} className="animate-spin text-[#00C2D6] mx-auto mb-3" />
        <div className="text-[#9CA8B8] text-sm">Loading your dashboard…</div>
      </div>
    );
  }

  const aff = data.affiliate;
  const statusColor =
    aff.status === "approved" ? "#A6FF00" : aff.status === "suspended" ? "#EF4444" : "#F7B500";

  const stateColour = (st) => STATE_COLOURS[st] || "#9CA8B8";
  const cashoutStatus = (p) =>
    p.status === "pending_payment" ? "awaiting approval" : p.status === "paid" && p.method === "credit" ? "issued" : p.status;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="affiliate-dashboard">
      {/* Identity + referral link — the two things they need most, first */}
      <div className="pg-card p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="pg-micro mb-1">// Signed in as</div>
            <div className="text-[#F7FAFC] text-xl sm:text-2xl pg-heading truncate" style={{ fontFamily: "'Rajdhani'" }}>
              {aff.name}
            </div>
            <div className="text-[#9CA8B8] text-xs sm:text-sm truncate">{aff.email}</div>
            <div className="mt-2 flex items-center gap-2 text-[10px] sm:text-[11px] pg-mono uppercase tracking-[0.18em] flex-wrap">
              <span style={{ color: statusColor }}>{aff.status}</span>
              <span className="text-[#9CA8B8]">·</span>
              <span className="text-[#00C2D6]" data-testid="aff-rate-line">
                {Math.round((data.rate || 0.1) * 100)}% per new customer ·{" "}
                {Math.round((data.rate || 0.1) * (data.credit_multiplier || 1.5) * 100)}% as store credit
              </span>
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem(TOKEN_KEY);
              onLogout();
            }}
            className="pg-btn-ghost text-xs shrink-0"
            data-testid="aff-logout"
          >
            Sign out
          </button>
        </div>

        <div className="mt-4 p-3 sm:p-4 bg-[#0B1220] border border-[#00C2D6]/30 rounded-sm">
          <div className="pg-micro mb-2" style={{ color: "#5EEAD4" }}>
            // Your referral link · code {aff.slug}
          </div>
          <div className="text-[#A6FF00] font-bold text-sm sm:text-base break-all mb-3" data-testid="aff-referral-url">
            {data.referral_url}
          </div>
          <div className="grid grid-cols-3 sm:flex gap-2">
            <button onClick={copyLink} className="pg-btn-primary text-xs justify-center" data-testid="aff-copy-link">
              {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button type="button" onClick={shareLink} className="pg-btn-ghost text-xs justify-center" data-testid="aff-share-link">
              <Share2 size={14} /> Share
            </button>
            <a
              className="pg-btn-ghost text-xs justify-center"
              href={`mailto:?subject=${encodeURIComponent(
                "Peptigen Labs — research peptides, UK same-day dispatch"
              )}&body=${encodeURIComponent(
                `Thought you'd want to see this: ${data.referral_url}\n\nPeptigen Labs supplies research-use-only peptides from a UK lab. Same-day dispatch before 14:00.\n\n(Research-use only. Not for human or animal consumption.)`
              )}`}
              data-testid="aff-email-share"
            >
              <Mail size={14} /> Email
            </a>
          </div>
          <div className="flex items-start gap-2 mt-3 text-[#9CA8B8] text-[11px] leading-relaxed">
            <Info size={12} className="text-[#00C2D6] shrink-0 mt-0.5" />
            <span>
              Always disclose (<span className="text-[#F7FAFC]">#ad</span> / <span className="text-[#F7FAFC]">#affiliate</span>) and never make
              medical or human-consumption claims.{" "}
              <Link to="/legal/affiliate-terms" className="text-[#00C2D6] hover:underline">Programme Terms</Link>.
            </span>
          </div>
        </div>
      </div>

      {/* Money first on mobile */}
      <BookmarkTip slug={aff.slug} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="aff-stats">
        <Stat label="// Pending (5 days)" value={`£${Number(data.pending_gbp || 0).toFixed(2)}`} accent="#F7B500" />
        <Stat label="// Available now" value={`£${Number(data.available_gbp || 0).toFixed(2)}`} accent="#A6FF00" />
        <Stat label="// Cashed out (value)" value={`£${Number(data.paid_out_value_gbp || 0).toFixed(2)}`} accent="#5EEAD4" />
        <Stat label="// All-time commission" value={`£${Number(data.total_commission_gbp || 0).toFixed(2)}`} />
      </div>

      {Number(data.requested_gbp || 0) > 0 && (
        <div className="text-[#9CA8B8] text-xs px-1" data-testid="aff-requested-note">
          <span className="text-[#F7B500] pg-mono">£{Number(data.requested_gbp).toFixed(2)}</span> is in a bank
          transfer awaiting our approval — it lands 3–5 working days after we approve it.
        </div>
      )}

      {data.held_gbp > 0 && (
        <div className="p-4 bg-[#0B1220] border border-[#F7B500]/40 rounded-sm text-[#F7B500] text-sm" data-testid="aff-held-notice">
          £{data.held_gbp.toFixed(2)} of commission is held pending a routine order review. It will be released automatically once cleared.
        </div>
      )}

      <CashOutPanel token={token} data={data} onDone={load} />

      <StepsStrip steps={steps} earned={data.orders.length > 0} earnedAmount={data.total_commission_gbp} />

      {/* Orders — cards on mobile, table on desktop */}
      <div className="pg-card p-4 sm:p-6" data-testid="aff-orders-table">
        <div className="flex items-baseline justify-between gap-3 mb-3 sm:mb-4">
          <div className="text-[#F7FAFC] text-lg pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
            Referred orders
          </div>
          {data.repeat_count > 0 && (
            <div className="text-[#9CA8B8] text-[11px]" data-testid="aff-repeat-note">
              {data.repeat_count} repeat order{data.repeat_count === 1 ? "" : "s"} — no commission (one per customer)
            </div>
          )}
        </div>
        {data.orders.length === 0 ? (
          <div className="text-[#9CA8B8] text-sm py-4">
            No paid orders yet. Share your link above and check back here — commission appears automatically once the customer completes payment.
          </div>
        ) : (
          <>
            <ul className="md:hidden divide-y divide-[#1E2E44]" data-testid="aff-orders-cards">
              {data.orders.map((o, i) => (
                <li key={`${o.order_ref}-${i}`} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[#F7FAFC] pg-mono text-xs">{o.order_ref} <span className="text-[#9CA8B8]">· {o.date}</span></div>
                    <div className="pg-mono text-[10px] uppercase tracking-[0.14em] mt-1" style={{ color: stateColour(o.state) }}>
                      {o.status}{o.state === "pending" ? ` · clears ${fmtDay(o.clears_at)}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[#A6FF00] font-semibold">£{o.commission_gbp.toFixed(2)}</div>
                    <div className="text-[#9CA8B8] text-[11px]">of £{Number(o.base_gbp || 0).toFixed(2)}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="pg-micro text-left border-b border-[#1E2E44]">
                    <th className="pb-2 pr-3">Ref</th>
                    <th className="pb-2 pr-3">Date</th>
                    <th className="pb-2 pr-3">Items after discount</th>
                    <th className="pb-2 pr-3">Into pot</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o, i) => (
                    <tr key={`${o.order_ref}-${i}`} className="border-b border-[#1E2E44]/50">
                      <td className="py-2 pr-3 text-[#F7FAFC] pg-mono text-xs">{o.order_ref}</td>
                      <td className="py-2 pr-3 text-[#9CA8B8] text-xs">{o.date}</td>
                      <td className="py-2 pr-3 text-[#9CA8B8]">£{Number(o.base_gbp || 0).toFixed(2)}</td>
                      <td className="py-2 pr-3 text-[#A6FF00] font-semibold">£{o.commission_gbp.toFixed(2)}</td>
                      <td className="py-2 pg-mono text-[11px] uppercase" data-testid={`aff-order-state-${o.order_ref.slice(1)}`}>
                        <span style={{ color: stateColour(o.state) }}>
                          {o.status}{o.state === "pending" ? ` · clears ${fmtDay(o.clears_at)}` : ""}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {data.payouts?.length > 0 && (
        <div className="pg-card p-4 sm:p-6" data-testid="aff-payouts-table">
          <div className="text-[#F7FAFC] text-lg pg-heading mb-3 sm:mb-4" style={{ fontFamily: "'Rajdhani'" }}>
            Cash-outs
          </div>
          <ul className="md:hidden divide-y divide-[#1E2E44]" data-testid="aff-cashout-cards">
            {data.payouts.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`aff-cashout-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-[#F7FAFC] text-xs">
                    {p.method === "credit" ? "Store credit" : "Bank transfer"} <span className="text-[#9CA8B8]">· {(p.requested_at || p.created_at || "").slice(0, 10)}</span>
                  </div>
                  {p.voucher_code && <div className="text-[#A6FF00] pg-mono text-[11px] mt-0.5 break-all">{p.voucher_code}</div>}
                  <div className="pg-mono text-[10px] uppercase tracking-[0.14em] mt-1" style={{ color: p.status === "paid" ? "#5EEAD4" : "#F7B500" }}>
                    {cashoutStatus(p)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[#A6FF00] font-semibold">£{Number(p.amount_gbp).toFixed(2)}</div>
                  <div className="text-[#9CA8B8] text-[11px]">pot £{Number(p.pot_gbp ?? p.amount_gbp).toFixed(2)}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="pg-micro text-left border-b border-[#1E2E44]">
                  <th className="pb-2 pr-3">Date</th>
                  <th className="pb-2 pr-3">Method</th>
                  <th className="pb-2 pr-3">Pot</th>
                  <th className="pb-2 pr-3">You get</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.payouts.map((p) => (
                  <tr key={p.id} className="border-b border-[#1E2E44]/50">
                    <td className="py-2 pr-3 text-[#F7FAFC] text-xs">{(p.requested_at || p.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 pr-3 text-[#9CA8B8] text-xs">
                      {p.method === "credit" ? "Store credit" : "Bank transfer"}
                      {p.voucher_code && <div className="text-[#A6FF00] pg-mono text-[11px] mt-0.5">{p.voucher_code}</div>}
                    </td>
                    <td className="py-2 pr-3 text-[#9CA8B8]">£{Number(p.pot_gbp ?? p.amount_gbp).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-[#A6FF00] font-semibold">£{Number(p.amount_gbp).toFixed(2)}</td>
                    <td className="py-2 pg-mono text-[11px] uppercase">
                      <span style={{ color: p.status === "paid" ? "#5EEAD4" : "#F7B500" }}>{cashoutStatus(p)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Less-frequent tools — collapsed on mobile */}
      <Foldable title="Share card & creatives" icon={Share2} testId="aff-fold-share" defaultOpen={typeof window !== "undefined" && window.innerWidth >= 1024}>
        <ShareCardButton token={token} referralUrl={data.referral_url} slug={aff.slug} onEngage={() => markStep("card")} />
      </Foldable>
      <Foldable
        title={aff.bank_details_complete ? "Bank details (on file)" : "Bank details — add to enable transfers"}
        icon={Landmark}
        testId="aff-fold-bank"
        defaultOpen={typeof window !== "undefined" && window.innerWidth >= 1024}
      >
        <BankDetailsPanel token={token} affiliate={aff} onSaved={load} />
      </Foldable>
    </div>
  );
}

function Foldable({ title, icon: Icon, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-[#0B1220] border border-[#1E2E44] rounded-sm text-left"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 text-[#F7FAFC] text-sm font-semibold">
          <Icon size={15} className="text-[#00C2D6]" /> {title}
        </span>
        {open ? <ChevronUp size={16} className="text-[#9CA8B8]" /> : <ChevronDown size={16} className="text-[#9CA8B8]" />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

// Soft-kill: programme is currently closed to new signups. Existing affiliates
// (anyone with a one-time magic-link or saved token) can still log in to view
// their dashboard — the backend keeps their referrals tracked so attribution
// isn't lost if we reopen. Flip this back to false to restore the public form.
const PROGRAMME_CLOSED_TO_NEW_SIGNUPS = true;

export default function Affiliates() {
  usePageMeta(
    "Affiliate Programme — Peptigen Labs",
    PROGRAMME_CLOSED_TO_NEW_SIGNUPS
      ? "The Peptigen Labs affiliate programme is currently closed to new applicants. Existing affiliates can sign in to view their account."
      : "Earn 10% on every order through your link — or 15% as instant store credit — referring UK research labs to Peptigen Labs. Cash out when you like, ready-made creatives, instant approval."
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("t");
  const [token, setToken] = useState(tokenFromUrl || localStorage.getItem(TOKEN_KEY) || null);

  useEffect(() => {
    if (tokenFromUrl) {
      localStorage.setItem(TOKEN_KEY, tokenFromUrl);
      setToken(tokenFromUrl);
      // Clean the token out of the URL to avoid accidental sharing of a signed link
      const next = new URLSearchParams(searchParams);
      next.delete("t");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl]);

  const onJoined = (t) => setToken(t);
  const onLogout = () => setToken(null);

  // If an existing affiliate is logged in, show their dashboard unchanged.
  if (token) {
    return (
      <>
        <Hero />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
          <Dashboard token={token} onLogout={onLogout} />
        </div>
      </>
    );
  }

  // Public pitch. While invite-only, the sign-up form is replaced by "Request an invite";
  // existing affiliates can still sign in via the magic-link form.
  return (
    <>
      <Hero inviteOnly={PROGRAMME_CLOSED_TO_NEW_SIGNUPS} />
      <TrustStrip />
      <Perks />
      <EarningsExample />
      <HowItWorks />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16" data-testid={PROGRAMME_CLOSED_TO_NEW_SIGNUPS ? "affiliate-closed-notice" : "affiliate-open-signup"}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 space-y-6">
            {PROGRAMME_CLOSED_TO_NEW_SIGNUPS ? <RequestInviteForm /> : <SignupForm onJoined={onJoined} />}
            <LoginLinkForm />
          </div>
          <aside className="lg:col-span-2 space-y-5">
            <ComplianceCard />
            <div className="pg-card p-6">
              <div className="text-[#F7FAFC] text-lg pg-heading mb-3" style={{ fontFamily: "'Rajdhani'" }}>
                Who it's for
              </div>
              <ul className="space-y-2 text-[#9CA8B8] text-sm leading-relaxed">
                <li>• Researchers and lab managers with colleagues who order regularly.</li>
                <li>• Science writers, newsletter and community owners.</li>
                <li>• Lab-supply and equipment affiliates adding a peptide partner.</li>
                <li>• Anyone tired of programmes that pay late, or not at all.</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
      <FAQ />
    </>
  );
}
